import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api, onSessionChange, refreshSession, setSession } from '../api/client'
import type { TokenResponse, User } from '../api/types'
import { AuthContext } from './useAuth'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = onSessionChange((s) => setUser(s?.user ?? null))
    refreshSession().finally(() => setLoading(false))
    return () => {
      unsubscribe()
    }
  }, [])

  // Renew the access token shortly before it expires so meetings never hit a 401 mid-action.
  useEffect(() => {
    if (!user) return
    const id = window.setInterval(() => void refreshSession(), 12 * 60 * 1000)
    return () => window.clearInterval(id)
  }, [user])

  const login = useCallback(async (email: string, password: string) => {
    setSession(await api<TokenResponse>('/auth/login', { method: 'POST', body: { email, password } }))
  }, [])

  // Accounts are invite-only: redeeming an invitation creates the account and signs in.
  const acceptInvitation = useCallback(async (token: string, displayName: string, password: string) => {
    setSession(
      await api<TokenResponse>('/auth/accept-invitation', {
        method: 'POST',
        body: { token, display_name: displayName, password },
      }),
    )
  }, [])

  const logout = useCallback(async () => {
    await api<void>('/auth/logout', { method: 'POST' }).catch(() => undefined)
    setSession(null)
  }, [])

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const value = useMemo(
    () => ({ user, loading, login, acceptInvitation, logout, isAdmin }),
    [user, loading, login, acceptInvitation, logout, isAdmin],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
